import { format, formatDistanceToNow, differenceInDays, differenceInMonths } from "date-fns";
import type { Subscription, PaymentHistory, LifecycleEvent, SubscriptionPeriod } from "@/types/subscription";

export interface OptimizationPromptData {
  subscription: Subscription;
  paymentHistory: PaymentHistory[];
  lifecycleEvents: LifecycleEvent[];
  subscriptionPeriods: SubscriptionPeriod[];
  userCurrency: string;
  categories: { name: string }[];
}

export interface PromptConfig {
  maxPayments?: number; // Default: 6
  maxLifecycleEvents?: number; // Default: 10
  maxCharacters?: number; // Default: 6000
}

interface PaymentSummary {
  totalPayments: number;
  totalSpent: number;
  averagePayment: number;
  consistency: string;
  recentPayments: PaymentHistory[];
  missedCount: number;
  refundedCount: number;
}

/**
 * Calculate payment consistency based on payment gaps
 */
function calculatePaymentConsistency(
  payments: PaymentHistory[],
  expectedFrequency?: string
): string {
  if (payments.length < 2) return "Insufficient data";
  
  const sortedPayments = [...payments]
    .filter(p => p.status === "paid")
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  
  if (sortedPayments.length < 2) return "Insufficient data";
  
  // Calculate expected days between payments
  const expectedDays: { [key: string]: number } = {
    weekly: 7,
    fortnightly: 14,
    monthly: 30,
    every_2_months: 60,
    quarterly: 90,
    yearly: 365,
  };
  
  const expected = expectedFrequency ? expectedDays[expectedFrequency] || 30 : 30;
  
  // Calculate actual gaps
  const gaps: number[] = [];
  for (let i = 1; i < sortedPayments.length; i++) {
    const daysDiff = differenceInDays(
      new Date(sortedPayments[i].date),
      new Date(sortedPayments[i - 1].date)
    );
    gaps.push(daysDiff);
  }
  
  // Calculate variance from expected
  const variances = gaps.map(gap => Math.abs(gap - expected));
  const avgVariance = variances.reduce((a, b) => a + b, 0) / variances.length;
  
  if (avgVariance <= 3) return "Regular";
  if (avgVariance <= 7) return "Mostly Regular";
  return "Irregular";
}

/**
 * Summarize payment history into key statistics and recent payments
 */
function summarizePaymentHistory(
  payments: PaymentHistory[],
  subscription: Subscription,
  maxPayments: number = 6
): PaymentSummary {
  const totalPayments = payments.length;
  const totalSpent = payments
    .filter(p => p.status === "paid")
    .reduce((sum, p) => sum + p.amount, 0);
  
  const paidPayments = payments.filter(p => p.status === "paid");
  const averagePayment = paidPayments.length > 0 ? totalSpent / paidPayments.length : 0;
  
  const consistency = calculatePaymentConsistency(payments, subscription.frequency);
  
  const recentPayments = [...payments]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, maxPayments);
  
  const missedCount = payments.filter(p => p.status === "missed").length;
  const refundedCount = payments.filter(p => p.status === "refunded").length;
  
  return {
    totalPayments,
    totalSpent,
    averagePayment,
    consistency,
    recentPayments,
    missedCount,
    refundedCount,
  };
}

/**
 * Format lifecycle pattern into concise summary
 */
function summarizeLifecyclePattern(
  subscription: Subscription,
  maxEvents: number = 10
): string {
  const { lifecycleEvents, subscriptionPeriods } = subscription;
  
  if (lifecycleEvents.length === 0) return "";
  
  // Calculate stats manually since we have lifecycleEvents and subscriptionPeriods
  const reactivationCount = lifecycleEvents.filter(e => e.event === "reactivated").length;
  const cancellationCount = lifecycleEvents.filter(e => e.event === "cancelled").length;
  
  const completedPeriods = subscriptionPeriods.filter(p => p.endDate);
  const averageActiveDays = completedPeriods.length > 0
    ? completedPeriods.reduce((sum, p) => sum + differenceInDays(new Date(p.endDate!), new Date(p.startDate)), 0) / completedPeriods.length
    : 0;
  
  let summary = "";
  
  // Pattern summary
  if (reactivationCount > 0) {
    summary += `- Reactivated ${reactivationCount}x (total ${reactivationCount + 1} activations)\n`;
  }
  
  if (cancellationCount > 0) {
    summary += `- Cancelled ${cancellationCount} time${cancellationCount > 1 ? "s" : ""}\n`;
  }
  
  if (averageActiveDays > 0 && completedPeriods.length > 1) {
    const avgMonths = Math.round(averageActiveDays / 30);
    summary += `- Average Active Period: ${avgMonths} months\n`;
  }
  
  // Recent events (limit to maxEvents)
  const recentEvents = [...lifecycleEvents]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, maxEvents);
  
  if (recentEvents.length > 0) {
    summary += `- Recent Events:\n`;
    for (const event of recentEvents.slice(0, 3)) {
      const dateStr = format(new Date(event.date), "MMM yyyy");
      const eventLabel = event.event.replace(/_/g, " ");
      const reason = event.reason ? ` (${event.reason})` : "";
      summary += `  - ${dateStr}: ${eventLabel}${reason}\n`;
    }
  }
  
  return summary;
}

/**
 * Build prompt sections with character tracking
 */
function buildPromptSections(
  data: OptimizationPromptData,
  config: Required<PromptConfig>
): { sections: string[]; totalChars: number } {
  const { subscription, paymentHistory, lifecycleEvents, subscriptionPeriods, userCurrency } = data;
  const sections: string[] = [];
  
  // Header
  sections.push(`[SUBSCRIPTION OPTIMIZATION ANALYSIS - ${subscription.name}]\n`);
  
  // Subscription Details (Required)
  let details = `## Subscription Details\n`;
  details += `- Service: ${subscription.name}\n`;
  
  if (subscription.amount && subscription.frequency) {
    details += `- Cost: ${subscription.amount.toFixed(2)} ${subscription.currency}/${subscription.frequency}\n`;
  } else if (subscription.billingType === "pay_as_you_go") {
    details += `- Billing: Pay-as-you-go (no fixed cost)\n`;
  }
  
  const startedAgo = formatDistanceToNow(new Date(subscription.startDate), { addSuffix: true });
  details += `- Status: ${subscription.status} | Started: ${format(new Date(subscription.startDate), "MMM yyyy")} (${startedAgo})\n`;
  
  if (subscription.tags && subscription.tags.length > 0) {
    details += `- Category: ${subscription.tags.join(", ")}\n`;
  }
  
  if (subscription.sentiment) {
    details += `- Rating: ${subscription.sentiment}/5\n`;
  }
  
  if (subscription.notes && subscription.notes.length <= 200) {
    details += `- Notes: ${subscription.notes}\n`;
  }
  
  sections.push(details);
  
  // Usage & Payment Summary (High Priority)
  if (paymentHistory.length > 0) {
    const summary = summarizePaymentHistory(paymentHistory, subscription, config.maxPayments);
    const duration = differenceInMonths(new Date(), new Date(subscription.startDate));
    
    let paymentSection = `## Usage & Payment Summary\n`;
    paymentSection += `- Lifetime: ${summary.totalPayments} payments, ${summary.totalSpent.toFixed(2)} ${userCurrency} over ${duration} months\n`;
    paymentSection += `- Average: ${summary.averagePayment.toFixed(2)} ${userCurrency}\n`;
    paymentSection += `- Consistency: ${summary.consistency}\n`;
    
    if (summary.recentPayments.length > 0) {
      const lastPayment = summary.recentPayments[0];
      paymentSection += `- Last Payment: ${format(new Date(lastPayment.date), "MMM yyyy")} - ${lastPayment.amount.toFixed(2)} ${lastPayment.currency}\n`;
    }
    
    if (summary.missedCount > 0 || summary.refundedCount > 0) {
      paymentSection += `- Issues: ${summary.missedCount} missed, ${summary.refundedCount} refunded\n`;
    }
    
    sections.push(paymentSection);
  }
  
  // Lifecycle Pattern (Conditional)
  if (lifecycleEvents.length > 1) {
    const pattern = summarizeLifecyclePattern(subscription, config.maxLifecycleEvents);
    if (pattern) {
      sections.push(`## Lifecycle Pattern\n${pattern}`);
    }
  }
  
  // Current Situation (High Priority)
  let situation = `## Current Situation\n`;
  
  if (subscription.nextPayment) {
    const daysUntil = differenceInDays(new Date(subscription.nextPayment), new Date());
    if (daysUntil < 0) {
      situation += `- Next Payment: ${Math.abs(daysUntil)} days overdue\n`;
    } else {
      situation += `- Next Payment: ${format(new Date(subscription.nextPayment), "MMM dd, yyyy")} (in ${daysUntil} days)\n`;
    }
  } else if (subscription.billingType === "pay_as_you_go") {
    situation += `- Next Payment: Pay-as-you-go (no scheduled payment)\n`;
  }
  
  if (subscription.status === "trial" && subscription.trialEndDate) {
    const trialDays = differenceInDays(new Date(subscription.trialEndDate), new Date());
    if (trialDays >= 0) {
      situation += `- ⚠️ Trial ending in ${trialDays} days\n`;
    }
  }
  
  if (subscription.autoRenewalDate) {
    const renewalDays = differenceInDays(new Date(subscription.autoRenewalDate), new Date());
    if (renewalDays >= 0 && renewalDays <= 30) {
      situation += `- ⚠️ Auto-renewal at full price in ${renewalDays} days\n`;
    }
  }
  
  if (subscription.status === "cancelled" && subscription.cancellationDate) {
    const cancelledAgo = formatDistanceToNow(new Date(subscription.cancellationDate), { addSuffix: true });
    situation += `- Cancelled ${cancelledAgo}\n`;
  }
  
  sections.push(situation);
  
  // What I'm Looking For (Required)
  let lookingFor = `## What I'm Looking For\n\nAnalyze this subscription and provide:\n\n`;
  lookingFor += `1. **Cost Savings**: Cheaper plans/tiers, annual discounts, competitors, family plans?\n`;
  lookingFor += `2. **Value Optimization**: Am I on the right plan for my usage? Unused features? Worthwhile upgrades?\n`;
  lookingFor += `3. **Timing**: Good time to negotiate/switch? Typical discount periods?\n`;
  lookingFor += `4. **Usage Tips**: How to get more value? Features to try?\n`;
  
  // Add reactivation analysis if cancelled with history
  const cancellationCount = lifecycleEvents.filter(e => e.event === "cancelled").length;
  if (subscription.status === "cancelled" && cancellationCount > 1) {
    // Calculate average break period from completed periods
    const completedPeriods = subscriptionPeriods.filter(p => p.endDate);
    if (completedPeriods.length > 1) {
      const avgActiveDays = completedPeriods.reduce((sum, p) => sum + differenceInDays(new Date(p.endDate!), new Date(p.startDate)), 0) / completedPeriods.length;
      const avgMonths = Math.round(avgActiveDays / 30);
      lookingFor += `5. **Reactivation**: Based on my ${avgMonths}-month break pattern, should I reconsider? Better alternatives for intermittent use?\n`;
    }
  }
  
  lookingFor += `\nProvide specific, actionable recommendations.\n`;
  sections.push(lookingFor);
  
  const totalChars = sections.join("\n").length;
  return { sections, totalChars };
}

/**
 * Truncate sections to stay within character limit
 */
function truncateToLimit(sections: string[], maxChars: number): string {
  let combined = sections.join("\n");
  
  if (combined.length <= maxChars) {
    return combined;
  }
  
  // If over limit, add truncation notice
  const truncationNotice = "\n\n[Some details truncated for brevity]";
  const targetLength = maxChars - truncationNotice.length;
  
  combined = combined.substring(0, targetLength);
  
  // Try to end at a clean line break
  const lastNewline = combined.lastIndexOf("\n");
  if (lastNewline > targetLength * 0.9) {
    combined = combined.substring(0, lastNewline);
  }
  
  return combined + truncationNotice;
}

/**
 * Generate optimization prompt for a subscription
 */
export function generateOptimizationPrompt(
  data: OptimizationPromptData,
  config: PromptConfig = {}
): string {
  const fullConfig: Required<PromptConfig> = {
    maxPayments: config.maxPayments ?? 6,
    maxLifecycleEvents: config.maxLifecycleEvents ?? 10,
    maxCharacters: config.maxCharacters ?? 6000,
  };
  
  const { sections, totalChars } = buildPromptSections(data, fullConfig);
  
  if (totalChars <= fullConfig.maxCharacters) {
    return sections.join("\n");
  }
  
  return truncateToLimit(sections, fullConfig.maxCharacters);
}
